<script setup>
import MatchJackpotListItem from '@/components/Match/MatchJackpotListItem.vue';
import DefaultButton from '@/components/Buttons/DefaultButton.vue';
import JackpotTickets from './Partials/JackpotTickets.vue';
import Spinner from '@/components/Spinner.vue';
import { computed, ref, watch, onMounted, onUnmounted} from 'vue';
import { storeToRefs } from 'pinia';
import { useFixturesStore } from '@/stores/fixtures';
import { useAuthStore } from '@/stores/auth';
import { useJackpotStore } from '@/stores/jackpot';
import { useResponseMessageStore } from '@/stores/response_message';
import { useI18n } from 'vue-i18n';
import { formatNumber } from '@/helpers';

const { t, tm } = useI18n();
const authStore = useAuthStore();
const fixtures = useFixturesStore();
const jackpot = useJackpotStore();
const responseMessageStore = useResponseMessageStore();
const { isAuth, getUser, getFixtureJackpots, getCredits, getCurrency } = storeToRefs(authStore);
const { getSendingJackpot } = storeToRefs(jackpot)
const activeTab = ref(1);
const events = ref([]);
const selectionsMap = ref({});
const amount = ref();
const winAmount = ref();
const currency = ref(getCredits.value[0]?.cuid._id);
const timeRemaining = ref(null)
let timerInterval = null
const tabs = ref({
  1: t('jackpots.3'),
  2: t('jackpots.2'),
  4: t('jackpots.5'),
});

const dailyJackpots = computed(() =>(getFixtureJackpots.value || []).filter(j => j.daily == true))
const weeklyJackpots = computed(() => (getFixtureJackpots.value || []).filter(j => j.daily == false))
const noDailyJackpots = computed(() => dailyJackpots.value.length == 0)
const noWeeklyJackpots = computed(() => weeklyJackpots.value.length == 0)
const getAmountForCurrency = list => list?.find(i => i.currency_id == currency.value)?.amount ?? 0;
const weeklyBetAmount = computed(() =>
  getAmountForCurrency(getUser.value?.fixtureJackpot?.jackpot_weekly_bet_amount)
);
const dailyBetAmount = computed(() =>
  getAmountForCurrency(getUser.value?.fixtureJackpot?.jackpot_daily_bet_amount)
);
const weeklyWinAmount = computed(() =>
  getAmountForCurrency(getUser.value?.fixtureJackpot?.jackpot_weekly_win_amount)
);
const dailyWinAmount = computed(() =>
  getAmountForCurrency(getUser.value?.fixtureJackpot?.jackpot_daily_win_amount)
);
const hasSelections = computed(() => 
  Object.keys(jackpot.selections).length > 0
);
const gameRuleSections = computed(() => {
  return tm('jackpots.game_rules.sections')
});
const setActiveTab = (tab) => activeTab.value = tab
const updateEvents = () => {
  const jackpots = getFixtureJackpots.value || []
  const isDaily = activeTab.value == 1
  const isWeekly = activeTab.value == 2
  if (isDaily || isWeekly) {
    const active = jackpots.find(j => j.daily == isDaily)
    events.value = active?.fixtures || []
    amount.value = isDaily ? dailyBetAmount.value : weeklyBetAmount.value
    winAmount.value = isDaily ? dailyWinAmount.value : weeklyWinAmount.value
  } else {
    events.value = []
    amount.value = null
    winAmount.value = null
  }
}
const activeJackpot = computed(() => {
  if (activeTab.value == 1) return weeklyJackpots.value[0]
  if (activeTab.value == 2) return dailyJackpots.value[0]
  return null
})
watch(activeTab, () => {
  updateEvents();
  jackpot.clearAll()
})
watch(activeJackpot, (j) => { updateTimer()})
watch(events, () => loadSelections(), { deep: true });

const loadSelections = async () => {
  const ids = events.value.map(ev => ev.i)
  if (!ids.length) {
    selectionsMap.value = {}
    return
  }
  const res = await fixtures.fetchSelections(ids, "pre", [20890])
  if (!res) {
    selectionsMap.value = {}
    return
  }
  selectionsMap.value = Object.fromEntries(
    res.map(ev => [ev.fi, ev.m || []])
  )
}

onMounted(async () => {
  await authStore.fetchFixtureJackpots();
  updateEvents()
  updateTimer()
  timerInterval = setInterval(updateTimer, 1000)
});

onUnmounted(() => {
  clearInterval(timerInterval)
})
const requiredEventIds = computed(() =>
  (events.value || []).map(ev => String(ev.i))
)
const missingEventIds = computed(() =>
  requiredEventIds.value.filter(id => {
    const sel = jackpot.selections?.[id]
    return !sel || Object.keys(sel).length === 0
  })
)
const canPlaceJackpot = computed(() =>
  requiredEventIds.value.length > 0 && missingEventIds.value.length === 0
)

const quickPick = () => { jackpot.quickPick(selectionsMap.value) }
const clearAll = () => { jackpot.clearAll() }

const placeJackpot = () => {
  if (!isAuth.value) {
    router.push({ name: 'login' })
    return
  }

  if (!canPlaceJackpot.value) {
    responseMessageStore.setMessage(t('jackpots.warning_message'))
    return
  }
  else{
    const slips = requiredEventIds.value.map(eventId => {
      const eventOdds = jackpot.selections[eventId]
      return Object.keys(eventOdds)[0]
    })

    const daily = activeTab.value == 1 ? true : false 
    jackpot.send(currency.value, slips, daily)
  }
}
const calculateRemainingTime = (jackpot) => {
  const now = Date.now()
  const start = jackpot.start_date
  let diff = Math.floor((start - now) / 1000)
  if (diff <= 0) {
    return '0d 0h 0m 0s'
  }
  const days = Math.floor(diff / 86400)
  diff %= 86400
  const hours = Math.floor(diff / 3600)
  diff %= 3600
  const minutes = Math.floor(diff / 60)
  const seconds = diff % 60
  return `${days}d ${hours}h ${minutes}m ${seconds}s`
}

const updateTimer = () => {
  if (!activeJackpot.value) {
    timeRemaining.value = null
    return
  }
  timeRemaining.value = calculateRemainingTime(activeJackpot.value)
}
</script>
<template>
  <Spinner :show="getSendingJackpot" />
  <div 
    v-if="getSendingJackpot"
    class="fixed inset-0 bg-black bg-opacity-50 z-50"
  ></div>
  <div>
    <div class="flex flex-1">
      <section :class="`lv-SportEvents`">
        <div class="jackpotBnr relative" @click="activeTab = 3">
          <p class="jackpotText" v-if="winAmount && ((activeTab == 1 && !noWeeklyJackpots) || (activeTab == 2 && !noDailyJackpots))">
            {{ formatNumber(winAmount) }} {{getCurrency(getCredits[0].cuid._id).cod}}
          </p>
          <p class="jackpotText" v-else-if="activeTab == 4"> {{ $t('jackpots.10') }} </p>
          <p class="jackpotText" v-else-if="activeTab == 3"> {{ $t('jackpots.game_rules.title') }} </p>
          <p class="jackpotText" v-else> {{ $t('jackpots.1') }} </p>
          <div class="text-white text-xs bg-[#1e211f] p-2 absolute bottom-0 left-0" v-if="timeRemaining">
            <span class="text-[10px] text-gray-300">{{ $t('jackpots.7') }}:</span>
            <span class="font-semibold ml-1">{{ timeRemaining }}</span>
          </div>
        </div>
        <div class="h-12 px-2 flex">
          <ul class="list-none flex items-center space-x-1 text-xs">
            <li
              v-for="tab, i in tabs"
              :key="i"
              :class="['tab-item', { active: activeTab == i }]"
              @click="setActiveTab(i)"
            >
              <span>{{ tab }}</span>
            </li>
          </ul>
        </div>
        <div class="border border-neutral-500/25" v-if="activeTab == 2 || activeTab == 1">                  
          <div v-if="(activeTab == 1 && noWeeklyJackpots) || (activeTab == 2 && noDailyJackpots)">
            <p class="text-center text-white py-10">
              {{ activeTab == 1 ? $t('jackpots.no_daily') : $t('jackpots.no_weekly') }}
            </p>
          </div>
          <div class="grid grid-cols-2 gap-2 m-2" v-else>
            <button
              v-if="hasSelections"
              @click="clearAll"
              class="clearBtn"
            >
                <span> {{ $t('general.27') }}</span>
            </button>
            <div v-else>
              <p class="jackpotBtn">
                <span>{{ events.length }} {{ $t('jackpots.6') }}</span>
                <span>{{formatNumber(winAmount)}} {{getCurrency(getCredits[0].cuid._id).cod}}</span>
              </p>  
            </div>
            <button
              @click="quickPick"
              class="quickBtn"
            >
              <span> {{ $t('general.28') }}</span>
            </button>
          </div>
        </div>
        <div  v-if="(activeTab == 1 && !noWeeklyJackpots) || (activeTab == 2 && !noDailyJackpots)">
          <ul class="list-none divide-y divide-neutral-500/25">
            <li 
              v-for="event in events"  
              :key="event.i"
              class="live-event-item"
              :data-i="event.i"
            > 
              <MatchJackpotListItem
                :event="event"
                :selections="selectionsMap[event.i] || []"
                :market="20890"
              />
            </li>
          </ul>
          <div>
            <DefaultButton
              class="bet-btn text-center bg-placebet text-base w-full"
              @click="placeJackpot"
            >
              <div class="flex items-center gap-2 ">
                {{$t('general.10')}}      
                <span v-if="amount">{{ formatNumber(amount) }} {{getCurrency(getCredits[0].cuid._id).cod}}</span>
              </div>
            </DefaultButton>
          </div>
        </div>
      </section>
    </div>
    <div v-if="activeTab == 3">
      <main class="flex-1 px-2 py-2 text-[15px] leading-relaxed text-white/80">
        <transition name="fade" mode="out-in">
          <div  class="bg-[#1e1e1e] rounded-xl p-4 border border-white/10 shadow-sm">
            <h2 class="text-lg font-semibold text-white mb-1">{{ $t('jackpots.game_rules.title') }}</h2>
            <p 
              v-for="(text, key) in gameRuleSections"
              :key="key"
              class="text-sm text-gray-400 mb-3"
            >
              {{ text }}
            </p>
          </div>
        </transition>
      </main>
    </div>
    <div v-if="activeTab == 4">
      <JackpotTickets />
    </div>
  </div>
</template>

<style scoped>
.lv-SportEvents {
    @apply relative flex-1;
}
.jackpotBnr{
    background: url(/public/jackpotBanner.jpg) no-repeat;
    background-size: cover;
    min-height: 108px;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
}
.jackpotText{
    height: 100%;
    font-size: 28px;
    font-weight: 800; 
    color: rgb(249, 249, 6);
}
.quickBtn{
    @apply w-full text-center text-[var(--color-primary4)] border-[var(--color-primary4)] text-sm font-medium shadow-md rounded-full border px-4 py-2 flex items-center justify-center
}
.clearBtn{
    @apply w-full text-center text-sm text-white font-medium shadow-md rounded-full border px-4 py-2 flex items-center justify-center
}
.jackpotBtn{
    @apply flex flex-col font-bold text-xs flex items-center justify-center w-full h-full text-[#F9F906]
}
</style>